import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { AttachmentsDetailsError, ClientId, NamesOfFieldEnum } from '~/typings/types';
import { useForm, useFormState } from 'react-final-form';
import { FormField } from '~/pages/questionnaires/shared';
import { PreviewLogo } from './preview-logo';
import { ButtonIcon } from '~/components/icon-button';
import { configTitleFieldsOfSelfEmployed } from '../../ui/steps/SelfEmployed/configTitleFields';
import { createGeneratedLogo, getGeneratedLogo } from '../../api/apiMethods';
import { clientApi, safeLocalStorage, sanitizeData } from '~/utils';
import { DRAFT_PAYFORM_TECH_DETAILS_V2, HELPER_FILE } from '../../api/apiUrls';
import { defaultAxiosRequestConfig } from '~/api/apiMethods';
import { safeBase64ToBlob } from '~/utils/safeBase64ToBlob';
import { debounce } from 'lodash';
import styles from './FieldGenerateLogo.module.scss';
import { message } from 'antd';

interface FieldGenerateLogoProps {
  clientId: ClientId;
  isLoadingMount: boolean;
  currentLogoSrc: string;
  setCurrentLogoSrc: Dispatch<SetStateAction<string>>;
  setLogoFileId: Dispatch<SetStateAction<number[]>>;
  logoError: AttachmentsDetailsError | null;
  setLogoError: Dispatch<SetStateAction<AttachmentsDetailsError | null>>;
  logoData: string;
  setLogoData: Dispatch<SetStateAction<string>>;
  setIsLogoProcessing: Dispatch<SetStateAction<boolean>>;
}

const configRequest = {
  ...defaultAxiosRequestConfig,
  headers: { 'Content-Type': 'multipart/form-data' },
};

const ALLOWED_LOGO_TYPES = ['.jpg', '.jpeg', '.png', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_LOGO_FILE_SIZE = 10 * 1024 * 1024; // 10 МБ

const validateLogoFile = (file: File): { isValid: boolean; errorMessage?: string } => {
  if (file.size === 0) {
    return { isValid: false, errorMessage: 'Файл пустой или поврежден' };
  }

  if (file.size > MAX_LOGO_FILE_SIZE) {
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      isValid: false,
      errorMessage: `Размер файла (${fileSizeMB} МБ) превышает допустимый размер 10 МБ`,
    };
  }

  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
  const fileMimeType = file.type.toLowerCase();

  const isTypeAllowed = ALLOWED_LOGO_TYPES.some(allowedType => {
    const normalized = allowedType.toLowerCase();
    if (normalized.startsWith('.')) return fileExtension === normalized;
    return fileMimeType === normalized;
  });

  if (!isTypeAllowed) {
    const allowedExtensions = ALLOWED_LOGO_TYPES.filter(t => t.startsWith('.')).map(ext => ext.substring(1).toUpperCase()).join(', ');
    return {
      isValid: false,
      errorMessage: `Недопустимый формат. Разрешенные форматы: ${allowedExtensions}`,
    };
  }

  return { isValid: true };
};

const getLogoRequirementsText = () => {
  const extensions = ALLOWED_LOGO_TYPES
    .filter(t => t.startsWith('.'))
    .map(ext => ext.substring(1).toUpperCase())
    .join(', ');
  const maxSizeMB = (MAX_LOGO_FILE_SIZE / (1024 * 1024)).toFixed(0);
  return `Поддерживаемые форматы: ${extensions} • до ${maxSizeMB} МБ`;
};

const processLogoError = (error: any): string => {
  if (error?.message?.includes('ERR_HTTP2_PROTOCOL_ERROR') ||
    error?.code === 'ERR_HTTP2_PROTOCOL_ERROR' ||
    error?.message?.includes('HTTP2') ||
    error?.message?.includes('protocol error')) {
    return 'Произошла временная ошибка соединения. Пожалуйста, попробуйте сгенерировать логотип еще раз через несколько секунд.';
  }

  if (error?.message?.includes('Network Error') ||
    error?.message?.includes('network') ||
    error?.code === 'NETWORK_ERROR') {
    return 'Проблемы с подключением к интернету. Проверьте соединение и попробуйте еще раз.';
  }

  if (error?.message?.includes('timeout') ||
    error?.message?.includes('Timeout') ||
    error?.code === 'ECONNABORTED') {
    return 'Превышено время ожидания ответа от сервера. Пожалуйста, попробуйте еще раз.';
  }

  const serverMessage = sanitizeData(error?.response?.data?.error?.[0]?.message) ||
    sanitizeData(error?.response?.data?.message) ||
    sanitizeData(error?.message);

  if (serverMessage) {
    return serverMessage;
  }

  return 'Не удалось сгенерировать логотип. Пожалуйста, попробуйте еще раз или загрузите свой логотип.';
};

export const FieldGenerateLogo = ({
  isLoadingMount,
  clientId,
  currentLogoSrc,
  setCurrentLogoSrc,
  setLogoFileId,
  logoError,
  setLogoError,
  logoData,
  setLogoData,
  setIsLogoProcessing,
}: FieldGenerateLogoProps) => {
  const [isLoadingGenerate, setIsLoadingGenerate] = useState(false);
  const [isLoadingUpload, setIsLoadingUpload] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const [isShowAllLogos, setIsShowAllLogos] = useState(false);
  const [isLoadFirstFromServer, setIsLoadFirstFromServer] = useState(false);
  const [logos, setLogos] = useState<Record<string, string>>({});
  const [isUserUploadedLogo, setIsUserUploadedLogo] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);
  const isPollingCancelledRef = useRef(false);
  const isGeneratingRef = useRef(false);

  const isAnyLoading = isLoadingGenerate || isLoadingUpload || isLoadingDelete || isLoadingMount;

  useEffect(() => {
    setIsLogoProcessing?.(isAnyLoading);
  }, [isAnyLoading]);

  const form = useForm();
  const { values } = useFormState();

  const latestValues = useRef({
    clientId,
    productType: '',
    productName: '',
    productDescription: '',
    hasUserLogo: false,
  });

  useEffect(() => {
    latestValues.current = {
      clientId,
      productType: values?.product_type,
      productName: values?.product_name,
      productDescription: values?.product_description,
      hasUserLogo: isUserUploadedLogo,
    };
  }, [values?.product_type, values?.product_name, values?.product_description, clientId, isUserUploadedLogo]);

  useEffect(() => {
    if (logoData && !isLoadFirstFromServer) {
      const blob = safeBase64ToBlob(logoData);
      const url = URL.createObjectURL(blob);
      if (currentLogoSrc?.startsWith('blob:')) URL.revokeObjectURL(currentLogoSrc);

      setCurrentLogoSrc(url);
      setIsLoadFirstFromServer(true);
    }
  }, [logoData]);

  const cancelPolling = () => {
    isPollingCancelledRef.current = true;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const uploadFileToServer = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await clientApi.post(HELPER_FILE, formData, configRequest);
      const newFileId = response?.data?.data?.file_id;

      if (newFileId) {
        await clientApi.post(DRAFT_PAYFORM_TECH_DETAILS_V2, {
          fields: { [NamesOfFieldEnum.LogoMainFileId]: Number(newFileId) },
          id: clientId,
          is_presave_mode: true,
        });
      }

      return newFileId;
    } catch (error: any) {
      throw new Error(processLogoError(error));
    }
  };

  const pollGeneratedLogos = async (
    clientId: string,
    maxAttempts = 150,
    interval = 2000
  ) => {
    isPollingCancelledRef.current = false;
    abortControllerRef.current = new AbortController();

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (isPollingCancelledRef.current) {
        throw new Error('Генерация логотипа отменена');
      }

      try {
        const res = await getGeneratedLogo(clientId);

        const status = res?.data?.status;
        const payload = res?.data?.data;

        if (status === 'completed' && payload) {
          return payload;
        }

        if (status === 'failed') {
          throw new Error('Не удалось сгенерировать логотип');
        }

        await new Promise((r) => setTimeout(r, interval));
      } catch (error: any) {
        if (isPollingCancelledRef.current) {
          throw new Error('Генерация логотипа отменена');
        }

        const message = error?.message || "";

        if (
          message.includes('ERR_HTTP2') ||
          message.includes('HTTP2')
        ) {
          await new Promise((r) => setTimeout(r, interval * 2));
          continue;
        }

        if (
          message.includes('Network Error') ||
          message.includes('ECONNRESET') ||
          message.includes('ECONNABORTED') ||
          message.includes('ETIMEDOUT') ||
          message.includes('Failed to fetch')
        ) {
          await new Promise((r) => setTimeout(r, interval));
          continue;
        }

        if (
          message.includes('AbortError') ||
          message.includes('ERR_CANCELED')
        ) {
          await new Promise((r) => setTimeout(r, interval));
          continue;
        }

        const status = error?.response?.status;
        if (status >= 500 && status < 600) {
          await new Promise((r) => setTimeout(r, interval));
          continue;
        }

        throw error;
      }
    }
    throw new Error('Истекло время ожидания генерации логотипа');
  };

  const generateLogo = async () => {
    if (isGeneratingRef.current) return;

    const { clientId, productType, productName, productDescription } = latestValues.current;
    if (!productType || !productName || !productDescription) {
      message.warning('Для генерации логотипа необходимо заполнить поле «Что продаёте?», «Название проекта», «Описание деятельности»"')
      return
    };

    isGeneratingRef.current = true;
    setIsLoadingGenerate(true);
    setIsUserUploadedLogo(false);
    try {
      await createGeneratedLogo(clientId, productType, productName, productDescription);
      const logosResult = await pollGeneratedLogos(clientId);

      if (!logosResult || Object.keys(logosResult).length === 0) {
        throw new Error('Не удалось получить сгенерированные логотипы');
      }

      setLogos(logosResult);
      const [firstId, firstBase64] = Object.entries(logosResult)[0];

      if (typeof firstBase64 !== 'string') {
        throw new Error('Неверный формат данных логотипа');
      }

      const blob = safeBase64ToBlob(firstBase64);
      const url = URL.createObjectURL(blob);
      if (currentLogoSrc?.startsWith('blob:')) URL.revokeObjectURL(currentLogoSrc);

      setCurrentLogoSrc(url);
      setLogoData(firstBase64);
      form.change('logo_main_file_id', [firstId]);

      const fileId = await uploadFileToServer(
        new File([blob], 'generated-logo.png', { type: 'image/png' })
      );

      setLogoFileId([fileId]);
      setLogoError(null);
    } catch (error: any) {
      if (error?.message === 'Генерация логотипа отменена') {
        return;
      }

      const errorMessage = processLogoError(error);

      const finalErrorMessage =
        errorMessage +
        ' Если проблема повторяется, вы можете загрузить логотип самостоятельно сейчас или добавить его позже в личном кабинете.';

      setLogoError({
        has: 1,
        message: finalErrorMessage,
      });
    } finally {
      isGeneratingRef.current = false;
      setIsLoadingGenerate(false);
      abortControllerRef.current = null;
      isPollingCancelledRef.current = false;
    }
  };

  // убрали автогенерацию логотипа при заполнении обязательных 3 полей
  // const debouncedGenerateLogo = useRef(debounce(generateLogo, 3000)).current;

  // useEffect(() => {
  //   const isEmptyLogo =
  //     values?.logo_main_file_id == null ||
  //     (Array.isArray(values?.logo_main_file_id) && values.logo_main_file_id.length === 0);

  //   if (isAnyLoading) return;

  //   if (
  //     values?.product_type &&
  //     values?.product_name &&
  //     values?.product_description &&
  //     isEmptyLogo &&
  //     !isUserUploadedLogo
  //   ) {
  //     debouncedGenerateLogo();
  //   }
  // }, [
  //   values?.product_type,
  //   values?.product_name,
  //   values?.product_description,
  //   values?.logo_main_file_id,
  //   isUserUploadedLogo
  // ]);

  const handleSelectLogo = (id: string, base64: string) => {
    const blob = safeBase64ToBlob(base64);
    const url = URL.createObjectURL(blob);
    if (currentLogoSrc?.startsWith('blob:')) URL.revokeObjectURL(currentLogoSrc);
    setCurrentLogoSrc(url);
    setLogoData(base64);
    form.change('logo_main_file_id', [id]);
    setIsUserUploadedLogo(false);
  };

  const handleUploadLogo = () => {
    cancelPolling();

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ALLOWED_LOGO_TYPES.join(',');
    input.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (!target.files || target.files.length === 0) return;

      const file = target.files[0];
      const validation = validateLogoFile(file);
      if (!validation.isValid) {
        setLogoError({ has: 1, message: validation.errorMessage || 'Ошибка валидации файла' });
        return;
      }

      const blobUrl = URL.createObjectURL(file);
      if (currentLogoSrc?.startsWith('blob:')) URL.revokeObjectURL(currentLogoSrc);
      setCurrentLogoSrc(blobUrl);

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = (reader.result as string)?.split(',')[1];
        setLogoData(base64);
      };
      reader.readAsDataURL(file);

      setIsLoadingUpload(true);
      setIsUserUploadedLogo(true);
      try {
        const fileId = await uploadFileToServer(file);
        setLogoFileId([fileId]);
        setLogoError(null);
        setLogos({});
        setIsShowAllLogos(false);
      } catch (error: any) {
        setLogoError({ has: 1, message: processLogoError(error) });
        setIsUserUploadedLogo(false);
      } finally {
        setIsLoadingUpload(false);
      }
    };
    input.click();
  };

  const handleDeleteLogo = () => {
    cancelPolling();

    setIsLoadingDelete(true);
    try {
      setLogoFileId([]);
      setCurrentLogoSrc('');
      setLogoError(null);
      setLogos({});
      setIsUserUploadedLogo(false);
      setIsShowAllLogos(false);
    } catch {
      setLogoError({ has: 1, message: 'Ошибка при удалении логотипа' });
    } finally {
      setIsLoadingDelete(false);
    }
  };

  useEffect(() => {
    return () => {
      cancelPolling();
    };
  }, []);

  const renderDescription = () => {
    if (isLoadingGenerate) return 'Создаём уникальный логотип на основе введеных вами данных...';
    if (isLoadingUpload) return 'Загружаем ваш логотип на сервер...';
    if (!currentLogoSrc && !isLoadingMount)
      return (
        <>
          Вы можете сгенерировать логотип или загрузить свой вариант по кнопке рядом.
          <br />
          {getLogoRequirementsText()}
        </>
      );
    if (Object.keys(logos).length > 0 && !isUserUploadedLogo)
      return (
        <div style={{ marginTop: 4 }}>
          <p>На основе заполненной информации мы сгенерировали логотип для вашей платёжной страницы.</p>
          <p>Вы можете сгенерировать еще раз или загрузить свой вариант по кнопке рядом.</p>
          <button
            type="button"
            onClick={() => setIsShowAllLogos((v) => !v)}
            className={styles.buttonShowAllLogos}
          >
            {isShowAllLogos ? 'Скрыть варианты' : 'Посмотреть ещё варианты'}
          </button>
        </div>
      );
    if (isUserUploadedLogo)
      return 'Вы загрузили собственный логотип.';
    return (
      <>
        Вы можете сгенерировать логотип или загрузить свой вариант по кнопке рядом.
        <br />
        {getLogoRequirementsText()}
      </>
    );
  };

  return (
    <FormField
      titleField={configTitleFieldsOfSelfEmployed[NamesOfFieldEnum.LogoMainFileId]}
      name={NamesOfFieldEnum.LogoMainFileId}
      tooltip="Логотип на платёжной странице усиливает доверие покупателя и повышает конверсию в оплату. Вы также можете добавить логотип позже в личном кабинете"
      styleContainer={{ minHeight: 150 }}
      renderError={logoError}
      shouldRenderTitleAboveField
      typeField="uploadFile"
    >
      <PreviewLogo
        isLoadingMount={isLoadingMount || isAnyLoading}
        src={currentLogoSrc}
        alt="Превью логотипа"
        actions={[
          <ButtonIcon
            key="generate"
            iconName="reload"
            variant="success"
            tooltip={(latestValues.current.productType && latestValues.current.productDescription && latestValues.current.productName) ? 'Сгенерировать логотип' : 'Для генерации логотипа необходимо заполнить поле «Что продаёте?», «Название проекта», «Описание деятельности»"'}
            onClick={generateLogo}
            disabled={isAnyLoading}
          />,
          <ButtonIcon
            key="upload"
            iconName="upload"
            variant="success"
            tooltip="Загрузить логотип"
            onClick={handleUploadLogo}
            disabled={isLoadingUpload || isLoadingDelete || isLoadingMount}
          />,
          <ButtonIcon
            key="delete"
            iconName="delete"
            variant="error"
            tooltip="Удалить логотип"
            onClick={handleDeleteLogo}
            disabled={isAnyLoading || !currentLogoSrc}
          />,
        ]}
        description={renderDescription()}
      />

      {isShowAllLogos && !isUserUploadedLogo && Object.keys(logos).length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 16 }}>
          {Object.entries(logos).map(([id, base64]) => (
            <img
              key={id}
              src={`data:image/png;base64,${base64}`}
              alt={`Logo ${id}`}
              onClick={() => handleSelectLogo(id, base64)}
              style={{
                width: 100,
                height: 100,
                objectFit: 'contain',
                border: logoData === base64
                  ? '2px solid #007bff'
                  : '1px solid #ccc',
                borderRadius: 8,
                cursor: 'pointer',
                padding: 4,
              }}
            />
          ))}
        </div>
      )}
    </FormField>
  );
};